import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import NoAccessPage from "./NoAccessPage";
import "react-datepicker/dist/react-datepicker.css";
import PaginationButtonToolbar from "../components/PaginatedButtonToolbar";
import CustomDateInputs from "../components/CustomDateInputs";
import {
  formatDateAndTimeOnline,
  formatDateAndTimeHourly,
  formatDateDaily,
} from "../components/DateAndTimeFunctions";
import useJWTInterceptors from "../authentication/useJWTInterceptors";

const SoundStationFullDataPaginated = () => {
  const isLoggedIn = useSelector((state) => state.login.accessToken);
  const { theRouteUrl } = useParams();
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationError = information.informationError;
  const informationLoading = information.informationLoading;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [device, setDevice] = useState();
  const [deviceId, setDeviceId] = useState();
  const [currentPageData, setCurrentPageData] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPageNumber, setTotalPageNumber] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [count, setCount] = useState(0);
  const [excelAPI, setExcelAPI] = useState("");
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState(false);
  const [soundDataType, setSoundDataType] = useState("spl");
  const [selectedSoundDataType, setSelectedSoundDataType] = useState("spl");
  const [pageIsValid, setPageIsValid] = useState(true);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const [selectedStartDateAndTime, setSelectedStartDateAndTime] =
    useState(yesterday);
  const [selectedEndDateAndTime, setSelectedEndDateAndTime] = useState(
    new Date()
  );
  const baseURL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (!informationLoading && !informationError && informationData) {
      const theSpecificStation = informationData.find(
        (x) => x.slug === theRouteUrl
      );
      setPageIsValid(!!theSpecificStation);

      const deviceNumber = theSpecificStation.device_id;
      setDeviceId(deviceNumber);

      if (theSpecificStation) {
        setDevice("OSPL");
      }
    }
  }, [informationLoading, informationError, informationData, theRouteUrl]);

  const historyApiUrl = useMemo(() => {
    if (!device || !deviceId || !baseURL) return null;

    let startDate, endDate, granularity;

    switch (soundDataType) {
      case "spl":
        startDate = formatDateAndTimeOnline(selectedStartDateAndTime);
        endDate = formatDateAndTimeOnline(selectedEndDateAndTime);
        granularity = 0;
        break;
      case "hourly":
        startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
        endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
        granularity = 1;
        break;
      case "daily":
        startDate = formatDateDaily(selectedStartDateAndTime);
        endDate = formatDateDaily(selectedEndDateAndTime);
        granularity = 2;
        break;
      case "nighttime":
        startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
        endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
        granularity = 3;
        break;
      case "daytime":
        startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
        endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
        granularity = 4;
        break;
      default:
        return null;
    }

    return `${baseURL}history/${
      soundDataType === "spl" ? "OSPL" : "LEQ"
    }/${deviceId}/${granularity}?start_date=${startDate}&end_date=${endDate}&page=${pageNumber}`;
  }, [
    device,
    deviceId,
    baseURL,
    soundDataType,
    selectedStartDateAndTime,
    selectedEndDateAndTime,
    pageNumber,
  ]);

  const excelUrl = useMemo(() => {
    if (!device || !deviceId || !baseURL) return null;

    let startDate, endDate, granularity;

    switch (soundDataType) {
      case "spl":
        startDate = formatDateAndTimeOnline(selectedStartDateAndTime);
        endDate = formatDateAndTimeOnline(selectedEndDateAndTime);
        granularity = 0;
        break;
      case "hourly":
        startDate = formatDateAndTimeHourly(
          formatDateAndTimeOnline(selectedStartDateAndTime)
        );
        endDate = formatDateAndTimeOnline(selectedEndDateAndTime);
        granularity = 1;
        break;
      case "daily":
        startDate = formatDateDaily(selectedStartDateAndTime);
        endDate = formatDateDaily(selectedEndDateAndTime);
        granularity = 2;
        break;
      case "nighttime":
        startDate = formatDateAndTimeHourly(
          formatDateAndTimeOnline(selectedStartDateAndTime)
        );
        endDate = formatDateAndTimeOnline(selectedEndDateAndTime);
        granularity = 23;
        break;
      case "daytime":
        startDate = formatDateAndTimeHourly(
          formatDateAndTimeOnline(selectedStartDateAndTime)
        );
        endDate = formatDateAndTimeOnline(selectedEndDateAndTime);
        granularity = 7;
        break;
      default:
        return null;
    }

    return `${baseURL}ospl_excel/?device_id=${deviceId}&start_date=${startDate}&end_date=${endDate}&granularity=${granularity}`;
  }, [
    device,
    deviceId,
    baseURL,
    soundDataType,
    selectedStartDateAndTime,
    selectedEndDateAndTime,
  ]);

  const getHistoryData = async () => {
    setSelectedSoundDataType(soundDataType);
    setLoading(true);
    try {
      const response = await useJWTInterceptors.get(
        `${historyApiUrl}&page=${pageNumber}`
      );
      setCurrentPageData(response.data.results);
      setTotalDataCount(response.data.count || 0);
      setTotalPageNumber(Math.ceil((response.data.count || 0) / 50));
      setError(false);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch latest data only when `count` or `pageNumber` changes
  useEffect(() => {
    if (!historyApiUrl) return; // Ensure API URL is ready

    getHistoryData();
  }, [count, pageNumber]); // Fetch only when `count` or `pageNumber` changes

  const downloadExcelReport = async () => {
    setExcelLoading(true);

    try {
      // Make a GET request to the server to fetch the Excel file
      const response = await useJWTInterceptors.get(excelUrl, {
        responseType: "blob", // set responseType to 'blob' for binary data
      });

      // Create a blob from the binary data received in the response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      // Create a URL for the blob and use it to create a link element
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      // Set the link's properties for download
      link.href = url;
      link.download = `${formatDateAndTimeOnline(
        selectedStartDateAndTime
      )}_${formatDateAndTimeOnline(selectedEndDateAndTime)}.xlsx`; // specify the desired file name

      // Append the link to the document body and trigger a click to start download
      document.body.appendChild(link);
      link.click();

      // Clean up the created elements after the download is initiated
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error("Error downloading Excel report:", error);
      setExcelError(error);
    } finally {
      setExcelLoading(false);
    }
    // console.log("excelLoading:", excelLoading);
  };

  // Utility function for date formatting
  const convertToJalali = useCallback((x) => {
    if (x) {
      const A = x.slice(0, 10);
      const B = x.slice(11, 19);
      const C = new Date(A);
      const D = new Intl.DateTimeFormat("fa-IR").format(C);
      const E = D.concat(" ", "(", B, ")");
      return E;
    }
  }, []);

  if (!isLoggedIn) {
    return <NoAccessPage />;
  } else if (isLoggedIn) {
    return (
      <div className="container-fluid p-2 text-medium">
        <div className="row">
          <CustomDateInputs
            type="sound"
            startDateAndTime={selectedStartDateAndTime}
            endDateAndTime={selectedEndDateAndTime}
            setSelectedStartDateAndTime={setSelectedStartDateAndTime}
            setSelectedEndDateAndTime={setSelectedEndDateAndTime}
            setCount={setCount}
            setSoundDataType={setSoundDataType}
            count={count}
            excelLoading={excelLoading}
            downloadExcelReport={downloadExcelReport}
            setPageNumber={setPageNumber}
          />
          <div className="col-lg-9 col-xl-10 " style={{ overflowX: "scroll" }}>
            <div dir="ltr" className="mb-1 d flex">
              {loading || error || currentPageData?.length < 1 ? null : (
                <PaginationButtonToolbar
                  type="sound"
                  setSelectedStartDateAndTime
                  soundDataType="online"
                  pageNumber={pageNumber}
                  totalPageNumber={totalPageNumber}
                  setPageNumber={setPageNumber}
                  count={count}
                />
              )}
            </div>

            {loading || error ? null : (
              <div className="bg-secondary p-1">
                <table
                  id="dtBasicExample"
                  className="table bg-light table-striped table-bordered table-sm mx-auto text-center"
                  style={{ overflowX: "scroll", maxWidth: "screen" }}
                  cellSpacing="0"
                  dir="rtl"
                  width="90%"
                >
                  <thead>
                    <tr>
                      {selectedSoundDataType === "spl" ? (
                        <th className="IRANSansXE p-3">SPL</th>
                      ) : (
                        <th className="IRANSansXE p-3">LEQ</th>
                      )}
                      {selectedSoundDataType === "spl" ||
                      selectedSoundDataType === "hourly" ? null : (
                        <>
                          <th className="IRANSansXE p-3">
                            LEQ <sub>min</sub>
                          </th>
                          <th className="IRANSansXE p-3">
                            LEQ <sub>max</sub>
                          </th>
                        </>
                      )}

                      {selectedSoundDataType === "spl" ||
                      selectedSoundDataType === "hourly" ? (
                        <>
                          <th className="IRANSansXE p-3">دما</th>
                          <th className="IRANSansXE p-3">رطوبت</th>
                          <th className="IRANSansXE p-3">فشار</th>
                        </>
                      ) : null}

                      {selectedSoundDataType === "daily" ? (
                        <>
                          <th className="IRANSansXE p-3">بیشترین دما</th>
                          <th className="IRANSansXE p-3">کمترین دما</th>
                          <th className="IRANSansXE p-3">رطوبت</th>
                          <th className="IRANSansXE p-3">فشار</th>
                        </>
                      ) : null}

                      <th className="IRANSansXE p-3">تاریخ و ساعت</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentPageData.map((station) => (
                      <tr
                        key={
                          station.ccreated_datetime || station.created_datetime
                        }
                      >
                        {selectedSoundDataType === "spl" &&
                        station.spl !== null &&
                        station.spl !== undefined ? (
                          <td className="IRANSansXB">
                            <p>
                              {Number(station.spl).toFixed(2)}
                              <sub>dB</sub>
                            </p>
                          </td>
                        ) : selectedSoundDataType !== "spl" &&
                          station.leq !== null &&
                          station.leq !== undefined ? (
                          <td className="IRANSansXB">
                            <p>
                              {Number(station.leq).toFixed(2)}
                              <sub>dB</sub>
                            </p>
                          </td>
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {selectedSoundDataType !== "spl" &&
                          selectedSoundDataType !== "hourly" && (
                            <>
                              {station.leq_min !== null &&
                              station.leq_min !== undefined ? (
                                <td className="IRANSansXB">
                                  <p>
                                    {Number(station.leq_min).toFixed(2)}
                                    <sub>dB</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.leq_max !== null &&
                              station.leq_max !== undefined ? (
                                <td className="IRANSansXB">
                                  <p>
                                    {Number(station.leq_max).toFixed(2)}
                                    <sub>dB</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}
                            </>
                          )}

                        {selectedSoundDataType === "spl" ? (
                          <>
                            {(station.temperature !== null &&
                              station.temperature !== undefined) ||
                            station.temperature === 0 ? (
                              <td className="IRANSansXB">
                                <p>
                                  {Number(station.temperature).toFixed(2)}
                                  <sub>℃</sub>
                                </p>
                              </td>
                            ) : (
                              <td className="IRANSansXB text text-center">-</td>
                            )}
                            {(station.humidity !== null &&
                              station.humidity !== undefined) ||
                            station.humidity === 0 ? (
                              <td className="IRANSansXB">
                                <p>
                                  <sub>%RH</sub>
                                  {Number(station.humidity).toFixed(2)}
                                </p>
                              </td>
                            ) : (
                              <td className="IRANSansXB text text-center">-</td>
                            )}
                            {(station.pressure_amb !== null &&
                              station.pressure_amb !== undefined) ||
                            station.pressure_amb === 0 ? (
                              <td className="IRANSansXB">
                                <p>
                                  {Number(station.pressure_amb).toFixed(2)}
                                  <sub>kPa</sub>
                                </p>
                              </td>
                            ) : (
                              <td className="IRANSansXB text text-center">-</td>
                            )}
                          </>
                        ) : null}

                        {selectedSoundDataType === "daily" ? (
                          <>
                            {(station.temperature_min !== null &&
                              station.temperature_min !== undefined) ||
                            station.temperature_min === 0 ? (
                              <td className="IRANSansXB">
                                <p>
                                  {Number(station.temperature_min).toFixed(2)}
                                  <sub>℃</sub>
                                </p>
                              </td>
                            ) : (
                              <td className="IRANSansXB text text-center">-</td>
                            )}
                            {(station.temperature_min !== null &&
                              station.temperature_min !== undefined) ||
                            station.temperature_min === 0 ? (
                              <td className="IRANSansXB">
                                <p>
                                  {Number(station.temperature_min).toFixed(2)}
                                  <sub>℃</sub>
                                </p>
                              </td>
                            ) : (
                              <td className="IRANSansXB text text-center">-</td>
                            )}
                          </>
                        ) : null}

                        {selectedSoundDataType === "hourly" ? (
                          <>
                            {(station.temperature_min !== null &&
                              station.temperature_min !== undefined) ||
                            station.temperature_min === 0 ? (
                              <td className="IRANSansXB">
                                <p>
                                  {Number(station.temperature_min).toFixed(2)}
                                  <sub>℃</sub>
                                </p>
                              </td>
                            ) : (
                              <td className="IRANSansXB text text-center">-</td>
                            )}
                          </>
                        ) : null}

                        {selectedSoundDataType === "hourly" ||
                        selectedSoundDataType === "daily" ? (
                          <>
                            {(station.humidity_avg !== null &&
                              station.humidity_avg !== undefined) ||
                            station.humidity_avg === 0 ? (
                              <td className="IRANSansXB">
                                <p>
                                  <sub>%RH</sub>
                                  {Number(station.humidity_avg).toFixed(2)}
                                </p>
                              </td>
                            ) : (
                              <td className="IRANSansXB text text-center">-</td>
                            )}
                            {(station.pressure_amb_avg !== null &&
                              station.pressure_amb_avg !== undefined) ||
                            station.pressure_amb_avg === 0 ? (
                              <td className="IRANSansXB">
                                <p>
                                  {Number(station.pressure_amb_avg).toFixed(2)}
                                  <sub>kPa</sub>
                                </p>
                              </td>
                            ) : (
                              <td className="IRANSansXB text text-center">-</td>
                            )}
                          </>
                        ) : null}

                        {station.ccreated_datetime ? (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.ccreated_datetime)}
                          </td>
                        ) : (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.created_datetime)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
};

export default SoundStationFullDataPaginated;
